import { Core } from "@faire/design-tokens";
import jobOpeningsPath from "@faire/web-api/routes/careers/openings";
import {
  getWindow,
  getWindowOrThrow,
} from "@faire/web/common/globals/getWindow";
import { ArrowRight } from "@faire/web/slate/icons/Arrow";
import { Column, Row } from "@faire/web/slate/Layout";
import { FaireLogo } from "@faire/web/slate/logos/FaireLogo";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { Hamburger } from "@faire/web/ui/Hamburger";
import { useViewportWidth } from "@faire/web/ui/hooks/useViewportWidth";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { Link } from "@faire/web/ui/LinkAnchor";
import { observer } from "mobx-react";
import React, { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import { SetWidthCTAButton } from "@faire/retailer/components/Header/desktop/HeaderCareersDesktop";
import { IHeaderProps } from "@faire/retailer/components/Header/HeaderCareers";
import {
  HeaderContainer,
  HeaderLink,
  HeaderLinkButton,
  SMALL_LOGO_WIDTH,
} from "@faire/retailer/components/Header/styled";
import { home } from "@faire/retailer/paths";

const HeaderCareersMobileBase: React.FC<IHeaderProps> = (props) => {
  const location = useLocation();
  const isOnJobOpeningsPage = location.pathname === jobOpeningsPath();
  const { pathList } = props;
  const viewportWidth = useViewportWidth((width) => width);
  const [windowY, setWindowY] = React.useState(getWindowOrThrow().scrollY);
  const [shouldShowMenu, setShouldShowMenu] = React.useState(false);
  /**
   * This the handle navigation callback deals with detecting scroll down
   * when the menu is opened behaviour is that when menu is opened,
   * when scroll down is detected, the menu will close
   */
  const handleNavigation = useCallback(() => {
    if (!shouldShowMenu) {
      return;
    }
    if (windowY < getWindowOrThrow().scrollY) {
      setShouldShowMenu(false);
    }
    setWindowY(getWindowOrThrow().scrollY);
  }, [shouldShowMenu, windowY]);

  useEffect(() => {
    setWindowY(getWindowOrThrow().scrollY);
    getWindow()?.addEventListener("scroll", handleNavigation);
    return () => {
      getWindow()?.removeEventListener("scroll", handleNavigation);
    };
  }, [handleNavigation]);

  useEffect(() => {
    setShouldShowMenu(false);
  }, [location]);

  return (
    <StyledHeaderContainer $menuOpened={shouldShowMenu}>
      <Row align="center">
        <HeaderLinkButton onClick={() => setShouldShowMenu(!shouldShowMenu)}>
          <Hamburger
            open={shouldShowMenu}
            color={shouldShowMenu ? Core.icon.inverse : Core.icon.default}
            data-test-id="mobile-menu"
          />
        </HeaderLinkButton>
        <StyledRow justify="space-between" align="center">
          <HeaderLink to={home()}>
            {viewportWidth <= SMALL_LOGO_WIDTH ? (
              <SmallFaireLogo inverse={shouldShowMenu} />
            ) : (
              <FaireLogo inverse={shouldShowMenu} />
            )}
          </HeaderLink>
          {isOnJobOpeningsPage ? null : (
            <SetWidthCTAButton
              variant={shouldShowMenu ? "secondary" : "primary"}
              linkTo={jobOpeningsPath()}
              data-test-id="job-openings-button"
            >
              <StyledSignUpButtonContent>
                <StrictLocalMsg
                  defaultMessage="All Job Openings"
                  description={{
                    text: "Button text to navigate to a job openings page",
                  }}
                />
                <StyledArrowRight />
              </StyledSignUpButtonContent>
            </SetWidthCTAButton>
          )}
        </StyledRow>
        <Spacer width="2x" />
      </Row>
      <MenuContainer $menuOpened={shouldShowMenu}>
        <Spacer width="8x" />
        <Column>
          <Spacer height="4x" />
          {pathList.map((pathObject) => {
            return (
              <Column key={pathObject.label}>
                <Typography>
                  <Link to={pathObject.path}>{pathObject.label}</Link>
                </Typography>
                <Spacer width="2x" />
              </Column>
            );
          })}
          <Spacer width="2x" />
        </Column>
      </MenuContainer>
    </StyledHeaderContainer>
  );
};

export const HeaderCareersMobile = observer(HeaderCareersMobileBase);

const MenuContainer = styled(Row)<{
  $menuOpened: boolean;
}>`
  width: 100%;
  position: absolute;
  bottom: 0px;
  background-color: ${Core.surface.inverse};
  transform: translateY(100%);
  max-height: ${(props) => (props.$menuOpened ? "500px" : "0px")};
  overflow: hidden;
  transition: max-height 0.5s linear;
`;

const SmallFaireLogo = styled(FaireLogo)`
  width: ${getSpacing("8x")};
`;

const StyledRow = styled(Row)`
  width: 100%;
`;

const StyledHeaderContainer = styled(HeaderContainer)<{
  $menuOpened: boolean;
}>`
  background-color: ${(props) =>
    props.$menuOpened ? Core.surface.default : Core.surface.inverse};
`;

const StyledArrowRight = styled(ArrowRight)`
  opacity: 0;
  transition: 0.5s ease;
  position: absolute;
  top: 0;
  bottom: 0;
  margin: auto 0;
  right: 2px;
`;

const SignUpButtonContent = styled.span`
  transition: 0.5s ease;
  position: relative;
  padding: 0 10px;
`;

const StyledSignUpButtonContent = styled(SignUpButtonContent)`
  padding: 0px;
`;
